import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import CpdTextField from '@/components/ui/CpdTextField';
import FacilityDropdown from '@/components/FacilityDropdown';
import SshKeyInput from './SshKeyInput';

const SftpConnectionBasicInfoSection = forwardRef(
  (
    {
      organizationId,
      onFormChange = () => {},
      fieldErrors = {},
      isEdit = false,
      initialData = null,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
      alias_name: '',
      description: undefined,
      host: '',
      port: '',
      username: '',
      password: undefined,
      folders: undefined,
      type: undefined,
      facility_id: undefined,
      notification_email: undefined,
      ssh_public_key: undefined,
      ssh_private_key: undefined,
      use_ssh_auth: false,
    });

    useEffect(() => {
      if (initialData) {
        setFormData({
          alias_name: initialData.alias_name,
          description: initialData.description,
          host: initialData.host,
          port: initialData.port,
          username: initialData.username,
          password: undefined, // INFO: Never populate password
          folders: initialData.folders || [],
          type: initialData.type,
          facility_id: initialData.facility_id,
          notification_email: initialData.notification_email,
          ssh_public_key: initialData.ssh_public_key,
          ssh_private_key: undefined, // INFO: Never populate private key
          use_ssh_auth: initialData.use_ssh_auth ?? false,
        });
      }
    }, [initialData]);

    useEffect(() => {
      onFormChange(formData);
    }, [formData, onFormChange]);

    const handleFormChange = (field, value) => {
      const optionalStringFields = [
        'description',
        'password',
        'type',
        'facility_id',
        'notification_email',
        'ssh_public_key',
        'ssh_private_key',
      ];
      if (optionalStringFields.includes(field) && (!value || value === '')) {
        setFormData((prev) => ({ ...prev, [field]: undefined }));
      } else {
        setFormData((prev) => ({ ...prev, [field]: value }));
      }
    };

    const handleAddFolder = () => {
      setFormData((prev) => ({
        ...prev,
        folders: prev.folders ? [...prev.folders, ''] : [''],
      }));
    };

    const handleRemoveFolder = (index) => {
      setFormData((prev) => {
        const updatedFolders = prev.folders.filter((_, i) => i !== index);
        return {
          ...prev,
          folders: updatedFolders.length > 0 ? updatedFolders : undefined,
        };
      });
    };

    const handleFolderChange = (index, value) => {
      setFormData((prev) => ({
        ...prev,
        folders: prev.folders.map((folder, i) =>
          i === index ? value : folder,
        ),
      }));
    };

    useImperativeHandle(ref, () => ({
      formData: formData,
    }));

    return (
      <Box>
        <Typography sx={{ mb: 1.5 }} variant='h6' gutterBottom>
          Connection Information
        </Typography>

        <CpdTextField
          label={t('sftpConnections.form.aliasName')}
          value={formData.alias_name}
          onChange={(e) => handleFormChange('alias_name', e.target.value)}
          required
          autoComplete='off'
          error={!!fieldErrors.alias_name}
          helperText={
            fieldErrors.alias_name || t('sftpConnections.form.aliasNameHelper')
          }
        />

        <CpdTextField
          label={t('sftpConnections.form.description')}
          value={formData.description ?? ''}
          onChange={(e) => handleFormChange('description', e.target.value)}
          multiline
          rows={3}
          autoComplete='off'
          error={!!fieldErrors.description}
          helperText={
            fieldErrors.description ||
            t('sftpConnections.form.descriptionHelper')
          }
        />

        <Divider sx={{ mb: 3, mt: 3 }} />

        <Typography variant='h6' gutterBottom>
          Server Details
        </Typography>

        <CpdTextField
          label={t('sftpConnections.form.host')}
          value={formData.host}
          onChange={(e) => handleFormChange('host', e.target.value)}
          required
          placeholder='sftp.example.com'
          autoComplete='off'
          error={!!fieldErrors.host}
          helperText={fieldErrors.host || t('sftpConnections.form.hostHelper')}
        />

        <CpdTextField
          label={t('sftpConnections.form.port')}
          value={formData.port}
          onChange={(e) =>
            handleFormChange('port', parseInt(e.target.value, 10) || 22)
          }
          type='number'
          inputProps={{ min: 1, max: 65535 }}
          autoComplete='off'
          error={!!fieldErrors.port}
          helperText={fieldErrors.port || t('sftpConnections.form.portHelper')}
        />

        <Divider sx={{ mb: 3, mt: 3 }} />

        <Typography variant='h6' gutterBottom>
          Authentication
        </Typography>

        <CpdTextField
          label={t('sftpConnections.form.username')}
          value={formData.username}
          onChange={(e) => handleFormChange('username', e.target.value)}
          required
          autoComplete='username'
          error={!!fieldErrors.username}
          helperText={
            fieldErrors.username || t('sftpConnections.form.usernameHelper')
          }
        />

        <CpdTextField
          label={t('sftpConnections.form.password')}
          value={formData.password ?? ''}
          onChange={(e) => handleFormChange('password', e.target.value)}
          type='password'
          required={!isEdit && !formData.use_ssh_auth}
          autoComplete='new-password'
          error={!!fieldErrors.password}
          helperText={
            fieldErrors.password || t('sftpConnections.form.passwordHelper')
          }
        />

        {isEdit && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 1, mb: 2 }}>
            Leave password blank to keep the current password
          </Typography>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={formData.use_ssh_auth ?? false}
              onChange={(e) =>
                handleFormChange('use_ssh_auth', e.target.checked)
              }
              name='use_ssh_auth'
              color='primary'
            />
          }
          label={t('sftpConnections.form.useSshAuth')}
          sx={{ mt: 1, mb: 2 }}
        />

        <SshKeyInput
          inputId='ssh_public_key'
          value={formData.ssh_public_key}
          onChange={(e) => handleFormChange('ssh_public_key', e.target.value)}
          labelKey='sftpConnections.form.sshPublicKey'
          helperKey='sftpConnections.form.sshPublicKeyHelper'
          placeholderKey='sftpConnections.form.sshPublicKeyPlaceholder'
          toastKey='sftpConnections.toast.sshPublicKeyCopied'
          error={!!fieldErrors.ssh_public_key}
          errorText={fieldErrors.ssh_public_key}
        />

        <SshKeyInput
          inputId='ssh_private_key'
          value={formData.ssh_private_key}
          onChange={(e) => handleFormChange('ssh_private_key', e.target.value)}
          labelKey='sftpConnections.form.sshPrivateKey'
          helperKey='sftpConnections.form.sshPrivateKeyHelper'
          placeholderKey='sftpConnections.form.sshPrivateKeyPlaceholder'
          toastKey='sftpConnections.toast.sshPrivateKeyCopied'
          error={!!fieldErrors.ssh_private_key}
          errorText={fieldErrors.ssh_private_key}
        />

        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mt: 1, mb: 2 }}>
          {isEdit
            ? t('sftpConnections.form.sshPrivateKeyLeaveBlank')
            : t('sftpConnections.form.sshKeysLeaveBlankCreate')}
        </Typography>

        <Divider sx={{ mb: 3, mt: 3 }} />

        <Typography variant='h6' gutterBottom>
          Configuration
        </Typography>

        <FacilityDropdown
          label={t('sftpConnections.form.facility')}
          value={formData.facility_id}
          onChange={(e) => handleFormChange('facility_id', e.target.value)}
          error={!!fieldErrors.facility_id}
          helperText={
            fieldErrors.facility_id || t('sftpConnections.form.facilityHelper')
          }
          placeholderKey='sftpConnections.form.facilityHelper'
          useFormsApi={true}
          organizationId={organizationId}
        />

        <CpdTextField
          label={t('sftpConnections.form.type')}
          value={formData.type ?? ''}
          onChange={(e) => handleFormChange('type', e.target.value)}
          select
          error={!!fieldErrors.type}
          helperText={fieldErrors.type || t('sftpConnections.form.typeHelper')}>
          <MenuItem value=''>
            <em>None</em>
          </MenuItem>
          <MenuItem value='facility_users'>
            {t('sftpConnections.filters.facilityUsers')}
          </MenuItem>
          <MenuItem value='facility_locations'>
            {t('sftpConnections.filters.facilityLocations')}
          </MenuItem>
        </CpdTextField>

        <CpdTextField
          label={t('sftpConnections.form.notificationEmail')}
          value={formData.notification_email ?? ''}
          onChange={(e) =>
            handleFormChange('notification_email', e.target.value)
          }
          multiline
          rows={3}
          error={!!fieldErrors.notification_email}
          helperText={
            fieldErrors.notification_email ||
            t('sftpConnections.form.notificationEmailHelper')
          }
        />

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant='subtitle1' sx={{ flexGrow: 1 }}>
              {t('sftpConnections.form.folders')}
            </Typography>
            <Button
              variant='outlined'
              size='small'
              startIcon={<AddIcon />}
              onClick={handleAddFolder}>
              {t('sftpConnections.form.addFolder')}
            </Button>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {t('sftpConnections.form.foldersHelper')}
          </Typography>
          {formData.folders?.map((folder, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CpdTextField
                value={folder}
                onChange={(e) => handleFolderChange(index, e.target.value)}
                placeholder='/incoming'
                autoComplete='off'
                error={!!fieldErrors[`folders[${index}]`]}
                helperText={fieldErrors[`folders[${index}]`]}
                sx={{ flexGrow: 1 }}
              />
              <IconButton
                onClick={() => handleRemoveFolder(index)}
                color='error'
                size='small'>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          {fieldErrors.folders && (
            <Typography variant='caption' color='error' sx={{ mt: 0.5 }}>
              {fieldErrors.folders}
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);

SftpConnectionBasicInfoSection.displayName = 'SftpConnectionBasicInfoSection';

export default SftpConnectionBasicInfoSection;
